/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useMutation, useQuery } from "@tanstack/react-query";
import imagePlaceholder from "../assets/ImgPlaceholder.svg";
import { getUserProfile } from "../api/Students/UserProfile";
import { useFormik } from "formik";
import MailIcon from "../components/Icons/MailIcon";
import CourseCard from "../components/Dashboard/Students/CourseCard";
import { Course } from "../types/types";
import Spinner from "../components/Shared/Spinner";
import { Link } from "react-router-dom";
import UploadProfile from "../components/Shared/UploadProfile";
import { EditStudentData } from "../api/Students/UpdateUserData";
import PencilIcon from "../components/Icons/PencilIcon";
import { toast } from "react-toastify";
import { queryClient } from "../main";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useUserDataStore } from "../stores/user";
import { Helmet } from 'react-helmet';
import { SettingsIcon } from "lucide-react";
import { getMyCustomQuizzes } from "../api/Certificates/GetMyCustomQuizzes";
import { getMyMockupExams } from "../api/Certificates/GetMyMockupExams";
import Button from "../components/Shared/Button";
const validationSchema = Yup.object({
    img_url: Yup.string().required("Image URL is required"),
});

const UserProfile = () => {
    const [activeTab, setActiveTab] = useState("courses");
    const [quizPage, setQuizPage] = useState(1);
    const [examPage, setExamPage] = useState(1);
    const limit = 6;

    const { data: studentProfile, isLoading: isStudentProfileLoading } = useQuery({
        queryFn: getUserProfile,
        queryKey: ["userprofile"],
    });

    const { data: quizzesData, isLoading: isQuizzesLoading } = useQuery({
        queryFn: () => getMyCustomQuizzes({ page: quizPage, limit }),
        queryKey: ["customQuizzes", quizPage],
    });

    const { data: mockupExamsData, isLoading: isMockupExamsLoading } = useQuery({
        queryFn: () => getMyMockupExams({ page: examPage, limit }),
        queryKey: ["mockupExams", examPage],
    });

    const student = studentProfile?.data;
    const courses = student?.courses || [];
    const quizzes = quizzesData?.data?.quizzes || [];
    const mockupExams = mockupExamsData?.data?.mockupExams || [];
    const { setUserData } = useUserDataStore(); // Access the setUserData function from the Zustand store

    const { mutate } = useMutation({
        mutationFn: EditStudentData,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["userprofile"] });
            toast.success("Image Changed successfully");
            //@ts-ignore
            setUserData(data.data);
        },
        onError: () => {
            formik.setSubmitting(false);
            toast.error("Error changing image");
        },
    });

    const formik = useFormik({
        initialValues: {
            img_url: "",
        },
        validationSchema,
        onSubmit: (values) => {
            if (student?.id) {
                //@ts-ignore
                mutate({ id: student.id, payload: values });
            }
        },
    });

    const handleimgUrlUpload = (url: string) => {
        formik.setFieldValue('img_url', url);
    };

    useEffect(() => {
        if (formik.values.img_url && formik.values.img_url.startsWith('http')) {
            formik.submitForm();
        }
    }, [formik.values.img_url]);

    const Pagination = ({ currentPage, totalPages, onPageChange }: {
        currentPage: number; totalPages: number; onPageChange: (page: number) => void;
    }) => (
        <div className="mt-6 flex justify-center items-center space-x-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 
                    ${currentPage === page
                            ? "bg-blue-500 text-white shadow-lg"
                            : "bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-600"
                        }`}
                >
                    {page}
                </button>
            ))}
        </div>
    );

    return (
        <div className="py-7 container min-h-screen">
            <Helmet>
                <link rel="canonical" href="https://www.nilewisect.com/my-profile" />
            </Helmet>

            <h1 className="text-burntBlack font-bold sm:text-[32px] text-xl mb-7  md:pt-32  pt-40 ">Student Profile</h1>
            <div className="flex gap-4 border-b">
                {["courses", "custom-quizzes", "mockup-exams"].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`sm:py-2 py-1 sm:px-4 px-2 font-medium transition-all sm:text-base text-xs duration-300 ${activeTab === tab ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-600"
                            }`}
                    >
                        {tab === "courses" ? "Courses" : tab === "custom-quizzes" ? "Custom Quizzes" : "Mockup Exams"}
                    </button>
                ))}
            </div>

            <div className='sm:p-14 pb-7 px-4 rounded-xl bg-white border border-black/15 shadow'>
                {isStudentProfileLoading ? (
                    <div className="min-h-[50vh] flex justify-center items-center">
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <form onSubmit={formik.handleSubmit} className="flex sm:gap-7 sm:pt-0 pt-8 gap-4 sm:items-center pb-9 border-b-2 border-black/15">
                            <div className="relative sm:size-[132px] rounded-full object-cover cursor-pointer h-full">
                                <UploadProfile folder="student_image" onFileUploaded={handleimgUrlUpload}>
                                    <div className="absolute z-50 sm:right-4 -right-2.5  sm:size-8 size-5 bg-[#2783E9] rounded-full flex items-center justify-center sm:bottom-7 bottom-0">
                                        <PencilIcon className="sm:size-auto size-2.5" />
                                    </div>
                                    {formik.values.img_url && formik.values.img_url !== "" && formik.values.img_url !== "uploading" ? (
                                        <img className="rounded-full size-20 sm:size-[110px] object-cover" src={formik.values.img_url} />
                                    ) : (
                                        <img className="rounded-full size-20 sm:size-[110px] object-cover" src={student?.img_url || imagePlaceholder} />
                                    )}
                                </UploadProfile>
                            </div>
                            <div>
                                <h1 className="font-bold sm:text-[32px] text-2xl sm:mb-3 mb-1.5">{student?.firstName} {student?.lastName}</h1>
                                <div className="flex sm:gap-1.5 gap-1 items-center w-fit py-2 px-3 bg-[#F3F4F6] rounded-full sm:mb-5 mb-2 ">
                                    <MailIcon className="sm:size-auto size-2.5" width={16} height={16} />
                                    <p className="text-[#565E6C] sm:text-sm text-[10px]">{student?.email}</p>
                                </div>
                                <Link to="/settings" className="text-[#2783E9] font-bold sm:text-base text-xs flex items-center gap-1">                      <SettingsIcon className="sm:size-auto size-4" />
                                    Account Settings</Link>
                            </div>
                        </form>
                        {activeTab === "courses" && (

                            <div className="py-5 space-y-8">
                                <h1 className="font-bold text-sm">{courses.length} items</h1>
                                <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 place-content-between gap-6">
                                    {courses.map((course: Course) => (
                                        <CourseCard key={course.id} course={course} />
                                    ))}
                                </div>
                            </div>
                        )}
                        {activeTab === "custom-quizzes" && (
                            <>
                                {isQuizzesLoading ? (
                                    <Spinner />
                                ) : (
                                    <div>
                                        <h1 className="font-bold text-sm py-5 ">{quizzesData?.data?.count} items</h1>
                                        {quizzes.length > 0 ? (
                                            <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
                                                {quizzes.map((quiz) => (
                                                    <div key={quiz.id} className="border border-black/15 flex flex-col  hover:border-secondary transition-all duration-300 p-4 rounded-lg">
                                                        <p className="font-bold sm:text-lg mb-2 mt-1">{quiz.quiz.title}</p>
                                                        <p className="capitalize sm:text-base text-sm">Score: {quiz.score}</p>
                                                        <p className="capitalize my-1 sm:text-base text-sm">Attempts: {quiz.num_tries}</p>
                                                        <p className={`capitalize font-semibold  mb-3 sm:text-base text-sm ${quiz.status === "passed" ? 'text-green-500' : 'text-red-500'}`}>Status: {quiz.status}</p>
                                                        <div className="flex flex-col gap-4">
                                                            <Link target="_blank" className="w-full mt-auto sm:text-base text-sm" to={`/generated-quiz/${quiz.quizId}`}>
                                                                <Button className="sm:!h-11 !h-9 w-full sm:!text-base !text-sm" variant="primary">Try again</Button>
                                                            </Link>
                                                            <Link target="_blank" className="w-full mt-auto sm:text-base text-sm" to={`/quiz-answers/${quiz.quizId}/${quiz.num_tries}`}>
                                                                <Button className="before:!bg-primary w-full hover:text-white sm:!text-base !text-sm !font-bold sm:!h-11 !h-9 !border-primary !border-2 text-primary"
                                                                    variant="transparent">See Result</Button>
                                                            </Link>
                                                        </div>
                                                    </div>
                                                ))}

                                            </div>
                                        ) : (
                                            <p>No quizzes found.</p>
                                        )}

                                        <Pagination
                                            currentPage={quizPage}
                                            totalPages={quizzesData?.data?.metaData?.totalPages || 1}
                                            onPageChange={setQuizPage}
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === "mockup-exams" && (
                            <>
                                {isMockupExamsLoading ? (
                                    <Spinner />
                                ) : (
                                    <div>
                                        <h1 className="font-bold text-sm py-5 ">{mockupExamsData?.data?.count} items</h1>
                                        {mockupExams.length > 0 ? (
                                            <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
                                                {mockupExams.map((exam) => (
                                                    <div key={exam.id} className="border border-black/15 flex flex-col  hover:border-secondary transition-all duration-300 p-4 rounded-lg">
                                                        <p className="font-bold sm:text-lg mb-2 mt-1">{exam.quiz.title}</p>
                                                        <p className="capitalize sm:text-base text-sm">Score: {exam.score}</p>
                                                        <p className="capitalize my-1 sm:text-base text-sm">Attempts: {exam.num_tries}</p>
                                                        <p className={`capitalize font-semibold  mb-3 sm:text-base text-sm ${exam.status === "passed" ? 'text-green-500' : 'text-red-500'}`}>Status: {exam.status}</p>
                                                        <div className="flex flex-col gap-4">
                                                            <div className="flex flex-col gap-4">
                                                            <Link target="_blank" className="w-full mt-auto sm:text-base text-sm" to={`/generated-quiz/${exam.quizId}`}>
                                                                <Button className="sm:!h-11 !h-9 w-full sm:!text-base !text-sm" variant="primary">Try again</Button>
                                                            </Link>
                                                            <Link target="_blank" className="w-full mt-auto sm:text-base text-sm" to={`/quiz-answers/${exam.quizId}/${exam.num_tries}`}>
                                                                <Button className="before:!bg-primary w-full hover:text-white sm:!text-base !text-sm !font-bold sm:!h-11 !h-9 !border-primary !border-2 text-primary"
                                                                    variant="transparent">See Result</Button>
                                                            </Link>
                                                        </div>
                                                        </div>
                                                    </div>
                                                ))}

                                            </div>
                                        ) : (
                                            <p>No mockup exams found.</p>
                                        )}

                                        <Pagination
                                            currentPage={examPage}
                                            totalPages={mockupExamsData?.data?.metaData?.totalPages || 1}
                                            onPageChange={setExamPage}
                                        />
                                    </div>
                                )}
                            </>
                        )}

                    </>
                )}
            </div>

        </div>
    );
}

export default UserProfile;
